export const CERTIFICATION_POSITION_CODES = {
	LEFT: "LEFT",
	CENTER: "CENTER",
	RIGHT: "RIGHT",
};

export const FIXED_CERTIFICATION_POSITION_POLICY_CODE =
	"TWO_SIGNATURES_HIGHER_RIGHT";

export const CERTIFICATION_SIGNATURE_TYPE_CODES = {
	DGA: "DGA",
	DIRECTOR_CAMPUS: "DIRECTOR_CAMPUS",
	JEFE_SERVICIO: "JEFE_SERVICIO",
	DOCENTE_PONENTE: "DOCENTE_PONENTE",
};

export const CERTIFICATION_SIGNATURE_SOURCE_STATUS = {
	PENDING: "PENDING",
	PENDING_CAMPUS: "PENDING_CAMPUS",
	PENDING_CAMPUS_REFRESH: "PENDING_CAMPUS_REFRESH",
	PENDING_EVENT_END_DATE: "PENDING_EVENT_END_DATE",
	PENDING_EVENT_TEACHER: "PENDING_EVENT_TEACHER",
	RESOLVED: "RESOLVED",
	RESOLVED_CATALOG: "RESOLVED_CATALOG",
	RESOLVED_EVENT_TEACHER: "RESOLVED_EVENT_TEACHER",
	INVALID_CAMPUS_OR_EVENT_END_DATE: "INVALID_CAMPUS_OR_EVENT_END_DATE",
	INVALID_EVENT_TEACHER_SIGNATURE: "INVALID_EVENT_TEACHER_SIGNATURE",
	AMBIGUOUS_EVENT_TEACHER_SIGNATURE:
		"AMBIGUOUS_EVENT_TEACHER_SIGNATURE",
};

const CERTIFICATION_SIGNATURE_TYPE_DEFINITIONS = [
	{
		id: 1,
		code: CERTIFICATION_SIGNATURE_TYPE_CODES.DGA,
		name: "DGA",
		hierarchyWeight: 3,
		requiresSpeakerData: false,
	},
	{
		id: 2,
		code: CERTIFICATION_SIGNATURE_TYPE_CODES.DIRECTOR_CAMPUS,
		name: "DIRECTOR CAMPUS",
		hierarchyWeight: 4,
		requiresSpeakerData: false,
	},
	{
		id: 3,
		code: CERTIFICATION_SIGNATURE_TYPE_CODES.JEFE_SERVICIO,
		name: "JEFE SERVICIO",
		hierarchyWeight: 2,
		requiresSpeakerData: false,
	},
	{
		id: 4,
		code: CERTIFICATION_SIGNATURE_TYPE_CODES.DOCENTE_PONENTE,
		name: "DOCENTE / PONENTE",
		hierarchyWeight: 1,
		requiresSpeakerData: true,
	},
];

const sortCertificationSignatureTypeOptions = (left, right) => {
	if (right.hierarchyWeight !== left.hierarchyWeight)
		return right.hierarchyWeight - left.hierarchyWeight;
	return left.id - right.id;
};

const buildCertificationSignatureTypeCatalog = definitions =>
	[...(definitions || [])]
		.map(item => ({
			id: Number(item?.id) || 0,
			code: item?.code || "",
			name: item?.name || "",
			hierarchyWeight: Number(item?.hierarchyWeight) || 0,
			requiresSpeakerData: Boolean(item?.requiresSpeakerData),
		}))
		.filter(item => Boolean(item.code))
		.sort(sortCertificationSignatureTypeOptions);

export const CERTIFICATION_SIGNATURE_TYPE_OPTIONS =
	buildCertificationSignatureTypeCatalog(
		CERTIFICATION_SIGNATURE_TYPE_DEFINITIONS
	);

const buildCertificationSignatureTypeOptionsByCode = options =>
	options.reduce((acc, item) => {
		acc[item.code] = item;
		return acc;
	}, {});

export const CERTIFICATION_SIGNATURE_TYPE_OPTIONS_BY_CODE =
	buildCertificationSignatureTypeOptionsByCode(
		CERTIFICATION_SIGNATURE_TYPE_OPTIONS
	);

let certificationSignatureTypeOptionsCatalog =
	CERTIFICATION_SIGNATURE_TYPE_OPTIONS;
let certificationSignatureTypeOptionsCatalogByCode =
	CERTIFICATION_SIGNATURE_TYPE_OPTIONS_BY_CODE;

export const AUTO_CERTIFICATION_CONTRACT = {
	BACKEND_FIELD: "certificacionAutomatica",
	API_EXPECTED_SHAPE:
		"enabled, campusId, signatureCount, positionPolicyCode, signatures[]",
	REQUEST_SHAPE: {
		enabled: "boolean",
		campusId: "number",
		signatureCount: "number",
		positionPolicyCode: "string",
		signatures: [
			{
				eventSignatureId: "number",
				eventSpeakerDirectoryId: "number",
				signatureTypeId: "number",
				signatureTypeCode: "string",
				utpCode: "string",
				fullName: "string",
				isManual: "boolean",
				hierarchyWeight: "number",
				positionCode: "string",
			},
		],
	},
	RESPONSE_ALIASES:
		"enabled|habilitado, campusId|idCampus, signatureCount|cantidadFirmas, positionPolicyCode|codigoPoliticaPosicion, signatures|firmas",
};

const getFirstDefinedValue = (...values) =>
	values.find(value => typeof value !== "undefined");

const getBackendAutoCertificateField = autoCertificate =>
	getFirstDefinedValue(
		autoCertificate,
		autoCertificate?.certificacionAutomatica,
		autoCertificate?.autoCertificate
	);

const getBackendSignatureField = (signature, ...fieldNames) =>
	getFirstDefinedValue(...fieldNames.map(fieldName => signature?.[fieldName]));

export const createEmptyCertificationSignature = (slotIndex = 0) => ({
	slotIndex,
	signatureTypeId: 0,
	signatureTypeCode: "",
	eventSignatureId: 0,
	eventSpeakerDirectoryId: 0,
	utpCode: "",
	fullName: "",
	isManual: false,
	hierarchyWeight: 0,
	positionCode: "",
	sourceStatus: CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING,
});

export const createEmptyAutoCertificate = () => ({
	enabled: false,
	campusId: 0,
	signatureCount: 0,
	positionPolicyCode: FIXED_CERTIFICATION_POSITION_POLICY_CODE,
	signatures: [],
});

export const sanitizeSignatureCount = value => {
	const count = Number(value) || 0;
	if (count < 1) return 0;
	return Math.min(count, 3);
};

export const syncCertificationSignatureTypeCatalog = (
	signatureTypeOptions = []
) => {
	if (!Array.isArray(signatureTypeOptions) || signatureTypeOptions.length === 0)
		return certificationSignatureTypeOptionsCatalog;
	const nextCatalog =
		buildCertificationSignatureTypeCatalog(signatureTypeOptions);
	if (nextCatalog.length === 0) return certificationSignatureTypeOptionsCatalog;
	certificationSignatureTypeOptionsCatalog = nextCatalog;
	certificationSignatureTypeOptionsCatalogByCode =
		buildCertificationSignatureTypeOptionsByCode(nextCatalog);
	return certificationSignatureTypeOptionsCatalog;
};

export const getCertificationSignatureTypeDetail = signatureTypeCode =>
	certificationSignatureTypeOptionsCatalogByCode[signatureTypeCode] || null;

export const getCertificationSignatureTypeName = signatureTypeCode =>
	getCertificationSignatureTypeDetail(signatureTypeCode)?.name || "-";

export const getCertificationSignatureHierarchyWeight = signatureTypeCode =>
	getCertificationSignatureTypeDetail(signatureTypeCode)?.hierarchyWeight || 0;

export const signatureRequiresSpeakerData = signatureTypeCode =>
	Boolean(
		getCertificationSignatureTypeDetail(signatureTypeCode)
			?.requiresSpeakerData
	);

export const isInstitutionalCertificationSignature = signatureTypeCode =>
	Boolean(
		signatureTypeCode &&
			!signatureRequiresSpeakerData(signatureTypeCode)
	);

export const getEventEndDateFromStep1 = step1 => {
	if (!step1?.part3?.duration) return 0;
	const { onlyDay, manyDays } = step1.part3.duration;
	if (manyDays?.date?.end > 0) return manyDays.date.end;
	if (onlyDay?.date > 0) return onlyDay.date;
	return 0;
};

export const buildCertificationSignature = (signature, slotIndex = 0) => {
	const base = {
		...createEmptyCertificationSignature(slotIndex),
		...(signature || {}),
		slotIndex,
	};
	const typeDetail = getCertificationSignatureTypeDetail(base.signatureTypeCode);
	return {
		...base,
		signatureTypeId:
			base.signatureTypeCode
				? base.signatureTypeId || typeDetail?.id || 0
				: 0,
		hierarchyWeight: base.signatureTypeCode
			? typeDetail?.hierarchyWeight || 0
			: 0,
		positionCode: "",
	};
};

export const getSelectedCertificationSignatureTypeCodes = (
	signatures = [],
	currentIndex = -1
) =>
	signatures
		.filter(
			(signature, index) =>
				index !== currentIndex && Boolean(signature.signatureTypeCode)
		)
		.map(({ signatureTypeCode }) => signatureTypeCode);

export const getAvailableCertificationSignatureTypeOptions = (
	signatureTypeOptions = [],
	signatures = [],
	currentIndex = -1
) => {
	const currentTypeCode = signatures[currentIndex]?.signatureTypeCode || "";
	const usedTypeCodes = new Set(
		getSelectedCertificationSignatureTypeCodes(signatures, currentIndex)
	);
	return signatureTypeOptions.filter(
		({ code }) => code === currentTypeCode || !usedTypeCodes.has(code)
	);
};

export const hasDuplicateSignatureTypeForIndex = (
	signatures = [],
	currentIndex = -1,
	signatureTypeCode = ""
) => {
	if (!signatureTypeCode) return false;
	return signatures.some(
		(signature, index) =>
			index !== currentIndex &&
			signature.signatureTypeCode === signatureTypeCode
	);
};

export const getCertificationTeacherReference = teacher => {
	if (!teacher) return null;
	if (teacher.sourceType || typeof teacher.fullName !== "undefined") {
		return {
			sourceType: teacher.sourceType || "",
			utpCode: teacher.utpCode || "",
			fullName: teacher.fullName || "",
			accountId: teacher.accountId || 0,
		};
	}
	if (teacher.search?.id || teacher.search?.fullName) {
		return {
			sourceType: "SEARCH",
			utpCode: teacher.search.id || "",
			fullName: teacher.search.fullName || "",
		};
	}
	if (teacher.free?.fullName || teacher.free?.accountId) {
		return {
			sourceType: "FREE",
			utpCode: "",
			fullName: teacher.free.fullName || "",
			accountId: teacher.free.accountId || 0,
		};
	}
	return null;
};

export const isCertificationTeacherReferenceResolved = teacher =>
	Boolean(getCertificationTeacherReference(teacher)?.fullName?.trim());

export const syncCertificationSignatureCount = (
	signatures = [],
	signatureCount = 0
) => {
	const count = sanitizeSignatureCount(signatureCount);
	if (count === 0) return [];
	const next = Array.from({ length: count }, (_, idx) =>
		buildCertificationSignature(signatures[idx], idx)
	);
	return next;
};

export const sortCertificationSignaturesByHierarchy = (signatures = []) => {
	return [...signatures].sort((left, right) => {
		if (right.hierarchyWeight !== left.hierarchyWeight)
			return right.hierarchyWeight - left.hierarchyWeight;
		return left.slotIndex - right.slotIndex;
	});
};

const getPositionsForSignatureCount = (
	signatureCount
) => {
	switch (sanitizeSignatureCount(signatureCount)) {
		case 1:
			return [CERTIFICATION_POSITION_CODES.CENTER];
		case 2:
			return [
				CERTIFICATION_POSITION_CODES.RIGHT,
				CERTIFICATION_POSITION_CODES.LEFT,
			];
		case 3:
			return [
				CERTIFICATION_POSITION_CODES.CENTER,
				CERTIFICATION_POSITION_CODES.RIGHT,
				CERTIFICATION_POSITION_CODES.LEFT,
			];
		default:
			return [];
	}
};

export const assignCertificationSignaturePositions = (
	signatures = []
) => {
	const filtered = signatures.filter(
		({ signatureTypeCode }) => Boolean(signatureTypeCode)
	);
	if (filtered.length === 0) return signatures;
	const sorted = sortCertificationSignaturesByHierarchy(filtered);
	const positions = getPositionsForSignatureCount(filtered.length);
	const positionsBySignature = sorted.reduce((acc, item, idx) => {
		acc[item.slotIndex] = positions[idx] || "";
		return acc;
	}, {});
	return signatures.map(signature => ({
		...signature,
		positionCode: positionsBySignature[signature.slotIndex] || "",
	}));
};

export const normalizeAutoCertificate = autoCertificate => {
	const base = {
		...createEmptyAutoCertificate(),
		...(autoCertificate || {}),
	};
	if (!base.enabled) return createEmptyAutoCertificate();
	const signatureCount = sanitizeSignatureCount(base.signatureCount);
	const signatures = syncCertificationSignatureCount(
		base.signatures,
		signatureCount
	).map((signature, idx) => buildCertificationSignature(signature, idx));
	return {
		...base,
		signatureCount,
		positionPolicyCode: FIXED_CERTIFICATION_POSITION_POLICY_CODE,
		signatures: assignCertificationSignaturePositions(
			signatures
		),
	};
};

export const reconcileAutoCertificateWithCampus = (
	autoCertificate,
	campusId
) => {
	const normalized = normalizeAutoCertificate(autoCertificate);
	if (!normalized.enabled) return normalized;
	if (!campusId) {
		return {
			...normalized,
			campusId: 0,
			signatures: normalized.signatures.map(signature => ({
				...signature,
				eventSignatureId: 0,
				sourceStatus: signatureRequiresSpeakerData(
					signature.signatureTypeCode
				)
					? signature.sourceStatus
					: CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING_CAMPUS,
			})),
		};
	}
	if (normalized.campusId === campusId) return normalized;
	return {
		...normalized,
		campusId,
		signatures: normalized.signatures.map(signature => {
			if (signatureRequiresSpeakerData(signature.signatureTypeCode))
				return signature;
			return {
				...signature,
				eventSignatureId: 0,
				utpCode: "",
				fullName: "",
				sourceStatus: signature.signatureTypeCode
					? CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING_CAMPUS_REFRESH
					: CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING,
			};
		}),
	};
};

const getAvailableInstitutionalSignatureByType = (
	availableSignatures = [],
	signatureTypeCode,
	currentEventSignatureId = 0
) => {
	const matches = availableSignatures.filter(
		item => item.signatureTypeCode === signatureTypeCode
	);
	if (matches.length === 0) return null;
	return (
		matches.find(
			item => item.eventSignatureId === currentEventSignatureId
		) || matches[0]
	);
};

const getAvailableSpeakerSignatureByTeacher = (
	availableSignatures = [],
	signatureTypeCode,
	teacherReference = null
) => {
	const normalizedTeacherCode =
		teacherReference?.utpCode?.trim().toUpperCase() || "";
	const normalizedTeacherName =
		teacherReference?.fullName?.trim().toUpperCase() || "";
	const resolveByName = () => {
		if (!normalizedTeacherName) {
			return { match: null, isAmbiguous: false };
		}
		const nameMatches = availableSignatures.filter(
			item =>
				item.signatureTypeCode === signatureTypeCode &&
				(item.displayName || "").trim().toUpperCase() === normalizedTeacherName
		);
		if (nameMatches.length > 1) {
			return { match: null, isAmbiguous: true };
		}
		return {
			match: nameMatches[0] || null,
			isAmbiguous: false,
		};
	};

	if (
		teacherReference?.sourceType === "SEARCH" &&
		normalizedTeacherCode
	) {
		const codeMatches = availableSignatures.filter(
			item =>
				item.signatureTypeCode === signatureTypeCode &&
				(item.utpCode || "").trim().toUpperCase() === normalizedTeacherCode
		);
		if (codeMatches.length > 1) {
			return { match: null, isAmbiguous: true };
		}
		if (codeMatches.length === 1) {
			return { match: codeMatches[0], isAmbiguous: false };
		}
		return resolveByName();
	}

	return resolveByName();
};

export const reconcileAutoCertificateWithAvailableSignatures = (
	autoCertificate,
	availableSignatures = [],
	{ campusId = 0, eventEndDate = 0, teacherReference = null } = {}
) => {
	const normalized = normalizeAutoCertificate(autoCertificate);
	if (!normalized.enabled) return normalized;
	const hasCampus = Boolean(campusId);
	const hasEventEndDate = Boolean(eventEndDate);
	const normalizedTeacherReference =
		getCertificationTeacherReference(teacherReference);
	return {
		...normalized,
		campusId: hasCampus ? campusId : 0,
		signatures: assignCertificationSignaturePositions(
			normalized.signatures.map(signature => {
				if (!signature.signatureTypeCode) return signature;
				if (signatureRequiresSpeakerData(signature.signatureTypeCode)) {
					if (!normalizedTeacherReference?.fullName?.trim()) {
						return {
							...signature,
							eventSignatureId: 0,
							utpCode: "",
							fullName: "",
							sourceStatus:
								CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING_EVENT_TEACHER,
						};
					}
					if (!hasCampus) {
						return {
							...signature,
							eventSignatureId: 0,
							utpCode: "",
							fullName: "",
							sourceStatus:
								CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING_CAMPUS,
						};
					}
					if (!hasEventEndDate) {
						return {
							...signature,
							eventSignatureId: 0,
							utpCode: "",
							fullName: "",
							sourceStatus:
								CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING_EVENT_END_DATE,
						};
					}
					const availableSignature =
						getAvailableSpeakerSignatureByTeacher(
							availableSignatures,
							signature.signatureTypeCode,
							normalizedTeacherReference
						);
					if (availableSignature.isAmbiguous) {
						return {
							...signature,
							eventSignatureId: 0,
							utpCode: normalizedTeacherReference.utpCode || "",
							fullName: "",
							sourceStatus:
								CERTIFICATION_SIGNATURE_SOURCE_STATUS.AMBIGUOUS_EVENT_TEACHER_SIGNATURE,
						};
					}
					if (!availableSignature.match) {
						return {
							...signature,
							eventSignatureId: 0,
							utpCode: normalizedTeacherReference.utpCode || "",
							fullName: "",
							sourceStatus:
								CERTIFICATION_SIGNATURE_SOURCE_STATUS.INVALID_EVENT_TEACHER_SIGNATURE,
						};
					}
					return {
						...signature,
						eventSignatureId: availableSignature.match.eventSignatureId,
						signatureTypeId:
							availableSignature.match.signatureTypeId ||
							signature.signatureTypeId,
						utpCode: availableSignature.match.utpCode || "",
						fullName: availableSignature.match.displayName || "",
						hierarchyWeight:
							availableSignature.match.hierarchyWeight ||
							signature.hierarchyWeight,
						sourceStatus:
							CERTIFICATION_SIGNATURE_SOURCE_STATUS.RESOLVED_CATALOG,
					};
				}
				if (!hasCampus) {
					return {
						...signature,
						eventSignatureId: 0,
						utpCode: "",
						fullName: "",
						sourceStatus:
							CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING_CAMPUS,
					};
				}
				if (!hasEventEndDate) {
					return {
						...signature,
						eventSignatureId: 0,
						utpCode: "",
						fullName: "",
						sourceStatus:
							CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING_EVENT_END_DATE,
					};
				}
				const availableSignature = getAvailableInstitutionalSignatureByType(
					availableSignatures,
					signature.signatureTypeCode,
					signature.eventSignatureId
				);
				if (!availableSignature) {
					return {
						...signature,
						eventSignatureId: 0,
						utpCode: "",
						fullName: "",
						sourceStatus:
							CERTIFICATION_SIGNATURE_SOURCE_STATUS.INVALID_CAMPUS_OR_EVENT_END_DATE,
					};
				}
				return {
					...signature,
					eventSignatureId: availableSignature.eventSignatureId,
					signatureTypeId:
						availableSignature.signatureTypeId || signature.signatureTypeId,
					utpCode: availableSignature.utpCode || "",
					fullName: availableSignature.displayName || "",
					hierarchyWeight:
						availableSignature.hierarchyWeight || signature.hierarchyWeight,
					sourceStatus:
						CERTIFICATION_SIGNATURE_SOURCE_STATUS.RESOLVED_CATALOG,
				};
			})
		),
	};
};

export const invalidateInstitutionalAutoCertificateSignatures = (
	autoCertificate,
	{ campusId = 0, eventEndDate = 0, teacherReference = null } = {}
) => {
	const normalized = normalizeAutoCertificate(autoCertificate);
	if (!normalized.enabled) return normalized;
	const normalizedTeacherReference =
		getCertificationTeacherReference(teacherReference);
	const nextStatus = !campusId
		? CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING_CAMPUS
		: !eventEndDate
		? CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING_EVENT_END_DATE
		: CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING_CAMPUS_REFRESH;
	return {
		...normalized,
		campusId: campusId || 0,
		signatures: assignCertificationSignaturePositions(
			normalized.signatures.map(signature => {
				if (!signature.signatureTypeCode) return signature;
				if (signatureRequiresSpeakerData(signature.signatureTypeCode)) {
					return {
						...signature,
						eventSignatureId: 0,
						utpCode: normalizedTeacherReference?.utpCode || "",
						fullName: "",
						sourceStatus: !normalizedTeacherReference?.fullName?.trim()
							? CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING_EVENT_TEACHER
							: nextStatus,
					};
				}
				return {
					...signature,
					eventSignatureId: 0,
					utpCode: "",
					fullName: "",
					sourceStatus: nextStatus,
				};
			})
		),
	};
};

export const hasInvalidInstitutionalSignatureValidity = (
	signatures = []
) =>
	signatures.some(
		signature =>
			signature.sourceStatus ===
				CERTIFICATION_SIGNATURE_SOURCE_STATUS.INVALID_CAMPUS_OR_EVENT_END_DATE ||
			signature.sourceStatus ===
				CERTIFICATION_SIGNATURE_SOURCE_STATUS.INVALID_EVENT_TEACHER_SIGNATURE ||
			signature.sourceStatus ===
				CERTIFICATION_SIGNATURE_SOURCE_STATUS.AMBIGUOUS_EVENT_TEACHER_SIGNATURE
	);

export const getInstitutionalSignatureValidityErrorText = (
	signature,
	organizerCampusName = ""
) => {
	if (
		signature?.sourceStatus ===
		CERTIFICATION_SIGNATURE_SOURCE_STATUS.AMBIGUOUS_EVENT_TEACHER_SIGNATURE
	) {
		return "Existe más de una firma válida para el docente / ponente del evento en el campus organizador. Revisa la configuración en el administrador de firmas.";
	}
	if (
		signature?.sourceStatus ===
		CERTIFICATION_SIGNATURE_SOURCE_STATUS.INVALID_EVENT_TEACHER_SIGNATURE
	) {
		return "No existe una firma activa y vigente para el docente / ponente del evento en el campus organizador.";
	}
	if (
		signature?.sourceStatus !==
		CERTIFICATION_SIGNATURE_SOURCE_STATUS.INVALID_CAMPUS_OR_EVENT_END_DATE
	)
		return "";
	const campusText = organizerCampusName
		? ` para ${organizerCampusName}`
		: "";
	return `No existe una firma institucional vigente${campusText} en la fecha fin del evento.`;
};

export const parseAutoCertificateFromApi = autoCertificate => {
	const backendAutoCertificate =
		getBackendAutoCertificateField(autoCertificate);
	const enabled = Boolean(
		getFirstDefinedValue(
			backendAutoCertificate?.enabled,
			backendAutoCertificate?.habilitado,
			false
		)
	);
	if (!enabled) return createEmptyAutoCertificate();
	const apiSignatures = getFirstDefinedValue(
		backendAutoCertificate?.signatures,
		backendAutoCertificate?.firmas,
		[]
	);
	return normalizeAutoCertificate({
		enabled,
		campusId:
			Number(
				getFirstDefinedValue(
					backendAutoCertificate?.campusId,
					backendAutoCertificate?.idCampus,
					0
				)
			) || 0,
		signatureCount: getFirstDefinedValue(
			backendAutoCertificate?.signatureCount,
			backendAutoCertificate?.cantidadFirmas,
			0
		),
		positionPolicyCode:
			getFirstDefinedValue(
				backendAutoCertificate?.positionPolicyCode,
				backendAutoCertificate?.codigoPoliticaPosicion,
				""
			) || FIXED_CERTIFICATION_POSITION_POLICY_CODE,
		signatures: apiSignatures.map((signature, slotIndex) => ({
				slotIndex,
				signatureTypeId:
					Number(
						getBackendSignatureField(
							signature,
							"signatureTypeId",
							"idTipoFirma"
						)
					) ||
					getCertificationSignatureTypeDetail(
						getBackendSignatureField(
							signature,
							"signatureTypeCode",
							"codigoTipoFirma"
						)
					)?.id ||
					0,
				signatureTypeCode:
					getBackendSignatureField(
						signature,
						"signatureTypeCode",
						"codigoTipoFirma"
					) || "",
				eventSignatureId:
					Number(
						getBackendSignatureField(
							signature,
							"eventSignatureId",
							"idFirmaEvento"
						)
					) || 0,
				eventSpeakerDirectoryId:
					Number(
						getBackendSignatureField(
							signature,
							"eventSpeakerDirectoryId",
							"idDirectorioPonenteEvento"
						)
					) || 0,
				utpCode:
					getBackendSignatureField(
						signature,
						"utpCode",
						"codigoUtp",
						"codigoDocente"
					) || "",
				fullName:
					getBackendSignatureField(
						signature,
						"fullName",
						"displayName",
						"nombreCompleto",
						"nombre"
					) || "",
				isManual: Boolean(
					getBackendSignatureField(
						signature,
						"isManual",
						"esManual"
					)
				),
				hierarchyWeight:
					Number(
						getBackendSignatureField(
							signature,
							"hierarchyWeight",
							"pesoJerarquia"
						)
					) ||
					getCertificationSignatureHierarchyWeight(
						getBackendSignatureField(
							signature,
							"signatureTypeCode",
							"codigoTipoFirma"
						)
					),
				positionCode:
					getBackendSignatureField(
						signature,
						"positionCode",
						"codigoPosicion"
					) || "",
				sourceStatus:
					getBackendSignatureField(
						signature,
						"sourceStatus",
						"estadoFuente"
					) || "RESOLVED",
			})),
	});
};

export const getAutoCertificatePayload = (
	autoCertificate,
	teacherReference = null
) => {
	const normalized = normalizeAutoCertificate(autoCertificate);
	if (!normalized.enabled)
		return {
			enabled: false,
		};
	return {
		enabled: true,
		campusId: normalized.campusId || 0,
		signatureCount: normalized.signatureCount,
		positionPolicyCode: FIXED_CERTIFICATION_POSITION_POLICY_CODE,
		signatures: normalized.signatures
			.filter(({ signatureTypeCode }) => Boolean(signatureTypeCode))
			.map(signature => ({
				...(signatureRequiresSpeakerData(signature.signatureTypeCode)
					? {
							utpCode:
								teacherReference?.utpCode?.trim() ||
								signature.utpCode?.trim() ||
								"",
							fullName:
								teacherReference?.fullName?.trim() ||
								signature.fullName?.trim() ||
								"",
					  }
					: {
							utpCode: signature.utpCode?.trim() || "",
							fullName:
								signature.fullName?.trim() ||
								getCertificationSignatureTypeName(
									signature.signatureTypeCode
								),
					  }),
				eventSignatureId: signature.eventSignatureId || 0,
				eventSpeakerDirectoryId: signature.eventSpeakerDirectoryId || 0,
				signatureTypeId:
					signature.signatureTypeId ||
					getCertificationSignatureTypeDetail(signature.signatureTypeCode)
						?.id ||
					0,
				signatureTypeCode: signature.signatureTypeCode,
				isManual: Boolean(signature.isManual),
				hierarchyWeight:
					signature.hierarchyWeight ||
					getCertificationSignatureHierarchyWeight(
						signature.signatureTypeCode
					),
				positionCode: signature.positionCode,
			})),
	};
};

export const getAutoCertificateSummaryRows = (
	autoCertificate,
	teacherReference = null
) => {
	const normalized = normalizeAutoCertificate(autoCertificate);
	if (!normalized.enabled)
		return [
			{
				field: "Generación automática de constancias:",
				description: "No",
			},
		];
	const rows = [
		{
			field: "Generación automática de constancias:",
			description: "Sí",
		},
		{
			field: "Cantidad de firmas:",
			description: String(normalized.signatureCount),
		},
	];
	normalized.signatures
		.filter(({ signatureTypeCode }) => Boolean(signatureTypeCode))
		.forEach((signature, idx) => {
			const typeName = getCertificationSignatureTypeName(
				signature.signatureTypeCode
			);
			const speakerSuffix = signatureRequiresSpeakerData(
				signature.signatureTypeCode
			)
				? ` - ${
						teacherReference?.fullName ||
						signature.fullName ||
						"Pendiente"
				  }`
				: "";
			rows.push({
				field: `Firma ${idx + 1}:`,
				description: `${signature.positionCode || "PENDIENTE"} - ${typeName}${speakerSuffix}`,
			});
		});
	return rows;
};

export const hasDuplicateSignatureTypes = (signatures = []) => {
	const signatureTypes = signatures
		.map(({ signatureTypeCode }) => signatureTypeCode)
		.filter(Boolean);
	return new Set(signatureTypes).size !== signatureTypes.length;
};

export const isCertificationSpeakerResolved = signature => {
	if (!signatureRequiresSpeakerData(signature.signatureTypeCode)) return true;
	return Boolean(signature.eventSignatureId > 0 && signature.fullName?.trim());
};
